import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaFacebookF, FaTwitter, FaLinkedinIn, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from 'react-icons/fa';
import './Footer.css';
import logo from '../assets/logo.png';
import { safeFetch } from '../utils/apiUtils';

const Footer = () => {
    const navigate = useNavigate();
    const [socialLinks, setSocialLinks] = useState({
        facebook: '#',
        twitter: '#',
        linkedin: '#'
    });
    const [contactInfo, setContactInfo] = useState({
        address: 'San Francisco, CA',
        phone: '+1 (555) 000-0000',
        email: 'hello@nimai.com'
    });
    const [services, setServices] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch socials, contact info, and services in parallel
                const [socialsResult, contactResult, servicesResult] = await Promise.all([
                    safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/social-links'),
                    safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/contact-pages'),
                    safeFetch(`https://aayushrealproperties.in/nimaibackend/public/api/services?t=${Date.now()}`)
                ]);

                // Update Social Links
                if (socialsResult.ok) {
                    const sData = socialsResult.data.data || socialsResult.data;
                    if (sData) setSocialLinks(sData);
                }

                // Update Contact Info
                if (contactResult.ok) {
                    let cData = null;
                    if (Array.isArray(contactResult.data)) cData = contactResult.data[0];
                    else if (contactResult.data && contactResult.data.data) cData = Array.isArray(contactResult.data.data) ? contactResult.data.data[0] : contactResult.data.data;
                    else if (contactResult.data) cData = contactResult.data;

                    if (cData) {
                        setContactInfo({
                            address: cData.physical_address || 'San Francisco, CA',
                            phone: cData.phone_number || '+1 (555) 000-0000',
                            email: cData.email_address || 'hello@nimai.com'
                        });
                    }
                }

                // Update Services
                if (servicesResult.ok) {
                    const servicesData = servicesResult.data.data || servicesResult.data;
                    setServices(Array.isArray(servicesData) ? servicesData : []);
                }
            } catch (error) {
                console.error('Error fetching footer data:', error);
            }
        };
        fetchData();
    }, []);

    const handleLinkClick = (path) => {
        navigate(path);
        window.scrollTo(0, 0);
    };

    const socials = [
        { Icon: FaFacebookF, url: socialLinks.facebook },
        { Icon: FaTwitter, url: socialLinks.twitter },
        { Icon: FaLinkedinIn, url: socialLinks.linkedin }
    ];

    return (
        <footer className="footer">
            <div className="footer-top">
                <div className="footer-brand">
                    <motion.div
                        className="footer-logo-container"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                    >
                        <img src={logo} alt="Nimai Solutions Logo" className="footer-logo-img" loading="lazy" />
                        <h3>Nimai Solutions</h3>
                    </motion.div>
                    <p>
                        A boutique digital agency delivering high-impact solutions for modern enterprises. Fueled by tech and driven by purpose.
                    </p>
                    <div className="social-icons">
                        {socials.map((social, i) => (
                            <motion.a
                                key={i}
                                href={social.url !== '#' ? social.url : undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-icon"
                                whileHover={{ y: -5, backgroundColor: "#FF9F1C" }}
                            >
                                <social.Icon />
                            </motion.a>
                        ))}
                    </div>
                </div>

                <div className="footer-col">
                    <h4>Services</h4>
                    <ul>
                        {services.length > 0 ? (
                            services.slice(0, 4).map((service) => (
                                <li key={service.id}>
                                    <a href="/#services">{service.title}</a>
                                </li>
                            ))
                        ) : (
                            <>
                                <li><a href="/#services">Cloud Infrastructure</a></li>
                                <li><a href="/#services">Custom Web Apps</a></li>
                                <li><a href="/#services">Mobile Development</a></li>
                                <li><a href="/#services">UI/UX Strategy</a></li>
                            </>
                        )}
                    </ul>
                </div>

                <div className="footer-col">
                    <h4>Company</h4>
                    <ul>
                        <li onClick={() => handleLinkClick('/about')} style={{ cursor: 'pointer' }}><span>About Us</span></li>
                        <li onClick={() => handleLinkClick('/portfolio')} style={{ cursor: 'pointer' }}><span>Our Portfolio</span></li>
                        <li onClick={() => handleLinkClick('/process')} style={{ cursor: 'pointer' }}><span>Our Process</span></li>
                        <li onClick={() => handleLinkClick('/contact')} style={{ cursor: 'pointer' }}><span>Careers</span></li>
                        <li onClick={() => handleLinkClick('/privacy-policy')} style={{ cursor: 'pointer' }}><span>Privacy Policy</span></li>
                    </ul>
                </div>

                <div className="footer-col">
                    <h4>Contact</h4>
                    <ul className="contact-info">
                        <li>
                            <FaMapMarkerAlt className="contact-icon" /> {contactInfo.address}
                        </li>
                        <li>
                            <FaPhoneAlt className="contact-icon" /> {contactInfo.phone}
                        </li>
                        <li>
                            <FaEnvelope className="contact-icon" /> {contactInfo.email}
                        </li>
                    </ul>
                </div>
            </div>

            <div className="footer-bottom">
                <p>&copy; 2026 Nimai Solutions. All rights reserved.</p>
                <div className="footer-bottom-links">
                    <span onClick={() => handleLinkClick('/terms-conditions')} style={{ cursor: 'pointer' }}>Terms of Service</span>
                    <span onClick={() => handleLinkClick('/cookie-policy')} style={{ cursor: 'pointer' }}>Cookie Policy</span>
                    <span onClick={() => handleLinkClick('/sitemap')} style={{ cursor: 'pointer' }}>Sitemap</span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
